import { ReactNode, useEffect, useRef, useState } from 'react';
import { AbsoluteFill, useDelayRender, useVideoConfig } from 'remotion';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapProvider } from './MapContext';

const COUNTRY_COLOR = "#fff";
const COUNTRY_HIGHLIGHT_COLOR = '#ffde99';
const BORDER_COLOR = "#636165";
const WATER_COLOR = "#87b7ce"

type Props = {
  /// https://en.wikipedia.org/wiki/ISO_3166-1_alpha-3 country codes
  highlighted_countries: string[];
  children?: ReactNode;
};

export const MapComponent: React.FC<Props> = ({highlighted_countries, children}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { delayRender, continueRender } = useDelayRender();
  const { width, height } = useVideoConfig();
  const [handle] = useState(() => delayRender('Loading map...'));
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    maplibregl.setWorkerUrl(
      URL.createObjectURL(
        new Blob(
          [`import "https://unpkg.com/maplibre-gl@${maplibregl.getVersion()}/dist/maplibre-gl-worker.mjs";`],
          { type: 'text/javascript' },
        ),
      ),
    );

    const map = new maplibregl.Map({
      container: ref.current,
      style: {
        name: "default",
        version: 8,
        sources: {
          "maplibre": {
            "url": "https://demotiles.maplibre.org/tiles/tiles.json",
            "type": "vector"
          }
        },
        layers: [
          {
            id: "water",
            type: "background",
            paint: {
              "background-color": WATER_COLOR
            }
          },
          {
            id: "countries",
            type: "fill",
            source: "maplibre",
            "source-layer": "countries",
            paint: {
              "fill-color": COUNTRY_COLOR
            }
          },
          {
            id: "borders",
            type: "line",
            source: "maplibre",
            "source-layer": "countries",
            paint: {
              "line-color": BORDER_COLOR
            }
          }
        ]
      },
      center: [13, 48],
      zoom: 3,
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
      },
    });
    setMap(map);

    map.on('load', () => {
      setMapLoaded(true);
      map.once('idle', () => continueRender(handle));
    });

    return () => {
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    if (!map.isStyleLoaded()) return;

    map.setPaintProperty('countries', 'fill-color', [
      'match',
      ['get', 'ADM0_A3'],
      highlighted_countries,
      COUNTRY_HIGHLIGHT_COLOR,
      COUNTRY_COLOR,
    ]);
  }, [map, highlighted_countries, mapLoaded]);

  return (
    <AbsoluteFill>
      <div ref={ref} style={{ width, height, position: 'absolute' }} />

      {map && (
        <MapProvider value={{ map, loaded: mapLoaded }}>
          {children}
        </MapProvider>
      )}
    </AbsoluteFill>
  );
};